import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BtnPrimary,
  BtnDefault,
  BtnDanger,
} from '../components/common/Buttons';
import TableWithSearchCTA from '../components/TableWithSearchCTA';
import Modal from '../components/common/Modal';
import IndustrySelect from '../components/IndustrySelect';

import { fetchJobListings } from '../hooks/queries';
import { deleteJobListing } from '../hooks/mutations';
import toast from 'react-hot-toast';

import { JobListing } from '../types/jobs';
import { useJobListingColumns } from '../components/columns/useJobListingCol';

const JobListings: React.FC = () => {
  const navigate = useNavigate();

  const {
    data: jobListings,
    error,
    isLoading,
    isFetching,
    refetch,
  } = fetchJobListings();

  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<JobListing | null>(null);

  const { mutate: deleteJob, isPending: isDeleting } = deleteJobListing({
    onSuccess: () => {
      toast.success('Job listing deleted successfully');
      setJobToDelete(null);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete job listing');
    },
  });

  const handleView = (job: JobListing) => navigate(`/job-listings/${job._id}`);
  const handleDelete = (job: JobListing) => setJobToDelete(job);
  const handleConfirmDelete = () => jobToDelete && deleteJob(jobToDelete);

  const columnDefs = useJobListingColumns({
    onView: handleView,
    onDelete: handleDelete,
  });

  return (
    <div className="w-full flex flex-col h-full">
      <TableWithSearchCTA
        rowData={jobListings?.data ?? []}
        ctas={{
          custom: (
            <BtnPrimary onClick={() => setIsPostModalOpen(true)}>
              Post a Job
            </BtnPrimary>
          ),
        }}
        columnDefs={columnDefs}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error as Error}
        refetch={refetch}
        headerTitle="Job Listings"
        description="Manage and organize job postings"
        enableRefresh
        storageKey="job_listings_table"
      />

      {/* Post Job Modal */}
      <Modal
        isOpen={isPostModalOpen}
        onRequestClose={() => setIsPostModalOpen(false)}
        title="Choose Category"
        className="!w-[25%] 2xl:!w-[20%]"
        height="fit-content"
      >
        <IndustrySelect jobListings={jobListings?.data ?? []} />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!jobToDelete}
        onRequestClose={() => setJobToDelete(null)}
        title="Confirm Delete"
        width="25%"
        height="fit-content"
      >
        <div className="space-y-4">
          <p className="text-sm text-blackTen">
            Are you sure you want to delete this job listing?
          </p>
          <div className="flex justify-end gap-2">
            <BtnDefault onClick={() => setJobToDelete(null)}>Cancel</BtnDefault>
            <BtnDanger onClick={handleConfirmDelete} disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete'}
            </BtnDanger>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default JobListings;
